/**
 * ApplicationFormPage — multi-step application form (/application/:id).
 * Mirrors Flutter WebCandidateApplicationScreen + WebCandidateMainForm and the step widgets
 * (web_resume, web_personal_information, web_education_experience, web_about_tata_communications,
 * web_integrity_questionnaire). Logic lives in useApplicationForm.
 */
import type { ReactNode } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { useLocalization } from '@/hooks/useLocalization';
import { Toast } from '@/features/home/components/Toast';
import { FlagIcon, LocationIcon } from '@/features/jobs/components/JobCardParts';
import {
  useApplicationForm,
  STEPS,
  STEP_LABELS,
  MAX_REPEATING_ENTRIES,
  errorKey,
  validationContextFor,
} from './hooks/useApplicationForm';
import type { FieldRef, SectionKey, ApplicationFormState } from './hooks/useApplicationForm';
import { ResumeUpload } from './components/ResumeUpload';
import { DynamicFormField } from './components/DynamicFormField';
import { isQuestionVisible } from './utils/formHelpers';
import type { ParsedApplication } from './utils/formHelpers';
import type { ComponentState, FldListState, ProfileInput } from '@/types/application.types';
import { FieldType } from '@/types/application.types';
import styles from './ApplicationFormPage.module.css';

/** Flutter WebCandidateApplicationFormConstants */
const CONSENT_TEXT =
  'I certify that my answers are true and complete to the best of my knowledge. If this application leads to employment, I understand that to the extent any of the information provided proves to be false or misleading, the Company may take disciplinary action, up to and including termination of employment';
const CONFIRM_EDIT_APPLIED =
  'You have already applied to this job. Do you want to edit the application submitted?';

type Actions = ReturnType<typeof useApplicationForm>['actions'];
type T = (key: string) => string;

export function ApplicationFormPage() {
  const { t } = useLocalization();
  const { state, actions } = useApplicationForm();
  const app = state.application;

  const stepCount = state.showQuestionInfo ? 5 : 4;
  const isBusy = state.isSaving || state.isUploading;

  // Without a form (e.g. /application with no job id) Flutter still shows the stepper and the
  // resume step; the later steps have nothing to render
  return (
    <PageLayout>
      <div className={styles.container}>
        {state.isInitializing ? (
          <FormShimmer />
        ) : (
          <>
            <JobSummary profileInput={state.profileInput} eyebrow={t('Application Form')} />

            <div className={styles.formCard}>
              <Stepper
                labels={STEP_LABELS.slice(0, stepCount).map((label) => t(label))}
                activeStep={state.activeStep}
                onSelect={actions.goToStep}
              />

              <div className={styles.stepBody} aria-busy={isBusy}>
                <div className={styles.stepHead}>
                  <h2 className={styles.stepTitle}>{t(STEP_LABELS[state.activeStep])}</h2>
                </div>

                {state.activeStep === STEPS.RESUME && (
                  <ResumeUpload
                    resumeUrl={state.resumeUrl}
                    resumeUploaded={state.resumeUploaded}
                    isLoading={state.isUploading}
                    isLoadingResumeData={state.isLoadingResumeData}
                    uploadProgress={state.uploadProgress}
                    onFileSelect={actions.selectResume}
                    onDownload={actions.downloadResume}
                    onDelete={actions.deleteResume}
                    showTitle={false}
                  />
                )}
                {app && state.activeStep === STEPS.PERSONAL_INFO && (
                  <PersonalStep app={app} state={state} actions={actions} t={t} />
                )}
                {app && state.activeStep === STEPS.EDUCATION_EXPERIENCE && (
                  <EducationExperienceStep app={app} state={state} actions={actions} t={t} />
                )}
                {app && state.activeStep === STEPS.ADDITIONAL_INFO && (
                  <AdditionalStep app={app} state={state} actions={actions} t={t} />
                )}
                {app && state.activeStep === STEPS.QUESTION_INFO && state.showQuestionInfo && (
                  <QuestionnaireStep app={app} state={state} actions={actions} t={t} />
                )}

                {isBusy && <div className={styles.busyVeil} aria-hidden="true" />}
              </div>

              <div className={styles.footer}>
                {state.activeStep > STEPS.RESUME ? (
                  <button
                    type="button"
                    className={styles.backBtn}
                    onClick={() => actions.goToStep(state.activeStep - 1)}
                    disabled={isBusy}
                  >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12.5 4.5 7 10l5.5 5.5" />
                    </svg>
                    {t('Back')}
                  </button>
                ) : (
                  <span />
                )}
                <div className={styles.footerEnd}>
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    disabled={isBusy || (state.activeStep === STEPS.RESUME && !state.resumeUploaded)}
                    // Keep focus in the field: its blur error would shift the button before the click lands
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={state.activeStep === STEPS.RESUME ? actions.continueFromResume : actions.saveAndContinue}
                  >
                    {state.isSaving && <span className={styles.btnSpinner} aria-hidden="true" />}
                    {t('Save & Continue')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {state.showContinuePopup && (
          <div className={styles.modalBackdrop} role="presentation">
            {/* Flutter CustomDialog.confirmDialog with no header: the question, Cancel and Proceed */}
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="edit-applied-text">
              <p id="edit-applied-text" className={styles.modalText}>{t(CONFIRM_EDIT_APPLIED)}</p>
              <div className={styles.modalActions}>
                <button type="button" className={styles.secondaryBtn} onClick={() => actions.dismissContinuePopup(false)}>
                  {t('Cancel')}
                </button>
                <button type="button" className={styles.primaryBtn} onClick={() => actions.dismissContinuePopup(true)} autoFocus>
                  {t('Proceed')}
                </button>
              </div>
            </div>
          </div>
        )}

        {state.toast && <Toast message={state.toast.message} type={state.toast.type} onClose={actions.clearToast} />}
      </div>
    </PageLayout>
  );
}

/* ────────────────────────────── Header + stepper ────────────────────────────── */

/** Job being applied for — shared with the Easy Apply page. */
export function JobSummary({ profileInput, eyebrow }: { profileInput: ProfileInput; eyebrow?: ReactNode }) {
  const { t } = useLocalization();
  const { jobTitle, jobDisplayId, jobLocation, skills } = profileInput;
  const skillList = (skills ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  return (
    <div className={styles.jobCard}>
      {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
      <h1 className={styles.jobTitle}>{jobTitle}</h1>
      <div className={styles.jobMeta}>
        {jobDisplayId && (
          <span className={styles.metaItem}>
            <FlagIcon />
            {t('Job ID')} {jobDisplayId}
          </span>
        )}
        {jobLocation && (
          <span className={styles.metaItem}>
            <LocationIcon />
            {jobLocation}
          </span>
        )}
      </div>
      {skillList.length > 0 && (
        <ul className={styles.skills}>
          {skillList.map((skill) => (
            <li key={skill} className={styles.skillChip}>{skill}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Stepper({
  labels,
  activeStep,
  onSelect,
}: {
  labels: string[];
  activeStep: number;
  onSelect: (step: number) => void;
}) {
  return (
    <div className={styles.stepperWrap}>
      <ol className={styles.stepper}>
        {labels.map((label, idx) => {
          const isDone = idx < activeStep;
          const isActive = idx === activeStep;
          return (
            <li key={label} className={styles.stepItem}>
              <button
                type="button"
                className={`${styles.stepBtn} ${isActive ? styles.stepActive : ''} ${isDone ? styles.stepDone : ''}`}
                onClick={() => onSelect(idx)}
                disabled={!isDone}
                aria-current={isActive ? 'step' : undefined}
              >
                <span className={styles.stepNumber}>
                  {isDone ? (
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m4.5 10.5 3.5 3.5 7.5-8" />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </span>
                <span className={styles.stepLabel}>{label}</span>
              </button>
            </li>
          );
        })}
      </ol>
      <div className={styles.progressTrack} aria-hidden="true">
        <span style={{ width: `${((activeStep + 1) / labels.length) * 100}%` }} />
      </div>
    </div>
  );
}

/* ────────────────────────────── Field rendering ────────────────────────────── */

interface StepProps {
  app: ParsedApplication;
  state: ApplicationFormState;
  actions: Actions;
  t: T;
}

/** Wide fields take the whole row. */
function isWide(field: FldListState): boolean {
  return field.type === FieldType.DESCRIPTION_TEXT_FLD || field.key === 'roleDescription';
}

function Field({
  refId,
  component,
  state,
  actions,
  monthsRef,
  footer,
}: {
  refId: FieldRef;
  component: ComponentState;
  state: ApplicationFormState;
  actions: Actions;
  /** The months field shown next to an EXP years field. */
  monthsRef?: FieldRef;
  footer?: ReactNode;
}) {
  const field = component.fields[refId.index];
  const months = monthsRef ? component.fields[monthsRef.index] : undefined;
  const section: SectionKey = refId.section;
  return (
    <div className={`${styles.fieldCell} ${isWide(field) ? styles.fieldWide : ''}`}>
      <DynamicFormField
        field={field}
        onChange={(value, display) => actions.updateField(refId, value, display)}
        error={state.errors[errorKey(refId)] ?? (monthsRef ? state.errors[errorKey(monthsRef)] : undefined)}
        validationContext={validationContextFor(section, component)}
        monthsField={months}
        onMonthsChange={monthsRef ? (value) => actions.updateField(monthsRef, value) : undefined}
        onCountryCodeChange={field.isMobileFld ? (code) => actions.updateCountryCode(refId, code) : undefined}
        onCurrencyChange={field.isCurrencyDropDown ? (code) => actions.updateCurrency(section, code) : undefined}
        footer={footer}
      />
    </div>
  );
}

function FieldGrid({
  section,
  component,
  state,
  actions,
  from = 0,
  entry,
  renderFooter,
}: {
  section: SectionKey;
  component: ComponentState;
  state: ApplicationFormState;
  actions: Actions;
  from?: number;
  entry?: number;
  renderFooter?: (field: FldListState) => ReactNode;
}) {
  return (
    <div className={styles.fieldsGrid}>
      {component.fields.map((field, index) =>
        index < from ? null : (
          <Field
            key={`${field.key || 'field'}-${index}`}
            refId={{ section, index, entry }}
            component={component}
            state={state}
            actions={actions}
            footer={renderFooter?.(field)}
          />
        ),
      )}
    </div>
  );
}

function Section({ title, children, aside }: { title?: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className={styles.section}>
      {(title || aside) && (
        <div className={styles.sectionHead}>
          {title && <h3 className={styles.sectionTitle}>{title}</h3>}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

/* ────────────────────────────── Steps ────────────────────────────── */

/**
 * Flutter WebPersonalInformation — personal details, address, experience pair + professional details.
 * Each section shows its headerTitle only when the form config has one.
 */
function PersonalStep({ app, state, actions }: StepProps) {
  const prof = app.professionalDetails;
  const hasExpPair = prof.fields[0]?.subType === 'EXP';
  const profSection: SectionKey = state.isIndia ? 'professionalDetails' : 'professionalDetailsNonIndia';
  const profComponent = app[profSection];

  return (
    <>
      <Section title={app.personalInformation.headerTitle || undefined}>
        <FieldGrid section="personalInformation" component={app.personalInformation} state={state} actions={actions} />
      </Section>

      {app.address.fields.length > 0 && (
        <Section title={app.address.headerTitle || undefined}>
          <FieldGrid section="address" component={app.address} state={state} actions={actions} />
        </Section>
      )}

      {(hasExpPair || profComponent.fields.length > 2) && (
        <Section title={prof.headerTitle || undefined}>
          <div className={styles.fieldsGrid}>
            {hasExpPair && (
              <Field
                refId={{ section: 'professionalDetails', index: 0 }}
                monthsRef={prof.fields[1] ? { section: 'professionalDetails', index: 1 } : undefined}
                component={prof}
                state={state}
                actions={actions}
              />
            )}
            {profComponent.fields.map((field, index) =>
              index < 2 ? null : (
                <Field
                  key={`${field.key}-${index}`}
                  refId={{ section: profSection, index }}
                  component={profComponent}
                  state={state}
                  actions={actions}
                />
              ),
            )}
          </div>
        </Section>
      )}
    </>
  );
}

/**
 * Flutter WebEducationExperience — repeating education (one highest) and experience entries.
 * A list shows its headerTitle; an empty list shows its name and "+ Add".
 */
function EducationExperienceStep({ app, state, actions, t }: StepProps) {
  const eduTitle = app.education.length > 0 ? app.education[0].headerTitle || undefined : t('Education');
  const expTitle = app.experience.length > 0 ? app.experience[0].headerTitle || undefined : t('Experience');
  const canAddEdu = app.education.length < MAX_REPEATING_ENTRIES;
  const canAddExp = app.experience.length < MAX_REPEATING_ENTRIES;

  return (
    <>
      <Section
        title={eduTitle}
        aside={
          <button type="button" className={styles.addBtn} onClick={() => actions.addEntry('education')} disabled={!canAddEdu}>
            {t('+ Add')}
          </button>
        }
      >
        <div className={styles.entries}>
          {app.education.map((edu, entry) => (
            <div key={entry} className={styles.entryCard}>
              <div className={styles.entryHead}>
                <span className={styles.entryTitle}>{t('Education')} {entry + 1}</span>
                <label className={styles.inlineCheck}>
                  <input
                    type="checkbox"
                    checked={edu.isHighestQualification}
                    onChange={() => actions.setHighestQualification(entry)}
                  />
                  {t('Highest Education')}
                  <span className={styles.required} aria-hidden="true">*</span>
                </label>
                {entry === app.education.length - 1 && app.education.length > 1 && (
                  <button type="button" className={styles.removeBtn} onClick={() => actions.removeLastEntry('education')}>
                    {t('Remove')}
                  </button>
                )}
              </div>
              <FieldGrid section="education" component={edu} entry={entry} state={state} actions={actions} />
            </div>
          ))}
        </div>
      </Section>

      <Section
        title={expTitle}
        aside={
          app.experience.length > 0 ? (
            <button type="button" className={styles.addBtn} onClick={() => actions.addEntry('experience')} disabled={!canAddExp}>
              {t('+ Add')}
            </button>
          ) : undefined
        }
      >
        {app.experience.length === 0 ? (
          <div className={styles.emptyEntries}>
            <button type="button" className={styles.addBtnSolid} onClick={() => actions.addEntry('experience')}>
              {t('+ Add')}
            </button>
          </div>
        ) : (
          <div className={styles.entries}>
            {app.experience.map((exp, entry) => (
              <div key={entry} className={styles.entryCard}>
                <div className={styles.entryHead}>
                  <span className={styles.entryTitle}>{t('Experience')} {entry + 1}</span>
                  {entry === app.experience.length - 1 && (
                    <button type="button" className={styles.removeBtn} onClick={() => actions.removeLastEntry('experience')}>
                      {t('Remove')}
                    </button>
                  )}
                </div>
                <FieldGrid
                  section="experience"
                  component={exp}
                  entry={entry}
                  state={state}
                  actions={actions}
                  renderFooter={(field) =>
                    field.key === 'endDate' ? (
                      <label className={styles.inlineCheck}>
                        <input
                          type="checkbox"
                          checked={field.isCurrentCompany}
                          onChange={() => actions.toggleCurrentCompany(entry)}
                        />
                        {t('Still working for this company')}
                      </label>
                    ) : null
                  }
                />
              </div>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

function ConsentCheck({ state, actions, t }: Omit<StepProps, 'app'>) {
  return (
    <label className={`${styles.consent} ${state.consentChecked ? styles.consentChecked : ''}`}>
      <input type="checkbox" checked={state.consentChecked} onChange={(e) => actions.setConsent(e.target.checked)} />
      <span>
        {t(CONSENT_TEXT)}
        <span className={styles.required} aria-hidden="true">*</span>
      </span>
    </label>
  );
}

/** Flutter WebAboutTataCommunications — additional info, certification, then "disclaimer signed". */
function AdditionalStep({ app, state, actions, t }: StepProps) {
  const fields = app.additionalInfo.fields;
  const hasDisclaimer = fields[fields.length - 1]?.key === 'disclaimerSigned';
  const last = fields.length - 1;
  return (
    <>
      <Section title={app.additionalInfo.headerTitle || undefined}>
        <div className={styles.fieldsList}>
          {fields.map((field, index) =>
            hasDisclaimer && index === last ? null : (
              <Field
                key={`${field.key}-${index}`}
                refId={{ section: 'additionalInfo', index }}
                component={app.additionalInfo}
                state={state}
                actions={actions}
              />
            ),
          )}
        </div>
      </Section>
      {!state.showQuestionInfo && <ConsentCheck state={state} actions={actions} t={t} />}
      {hasDisclaimer && (
        <div className={styles.fieldsList}>
          <Field refId={{ section: 'additionalInfo', index: last }} component={app.additionalInfo} state={state} actions={actions} />
        </div>
      )}
    </>
  );
}

/**
 * Flutter WebIntegrityQuestionnaire — numbered questions, conditional follow-ups, certification.
 * D2h: the numbering counts visible questions only (Flutter numbers by list position).
 */
function QuestionnaireStep({ app, state, actions, t }: StepProps) {
  const fields = app.questionInfo.fields;
  let number = 0;
  return (
    <>
      <Section title={app.questionInfo.headerTitle || undefined}>
        <div className={styles.questions}>
          {fields.map((field, index) => {
            if (!isQuestionVisible(field, fields)) return null;
            number += 1;
            const ref: FieldRef = { section: 'questionInfo', index };
            if (field.type !== FieldType.RADIO_BUTTON) {
              return (
                <div key={index} className={`${styles.questionCard} ${field.disableByKey ? styles.questionFollowUp : ''}`}>
                  <span className={styles.questionNumber}>{number}</span>
                  <div className={styles.questionBody}>
                    <Field refId={ref} component={app.questionInfo} state={state} actions={actions} />
                  </div>
                </div>
              );
            }
            const options = (field.data ?? []) as Array<{ label?: string; value?: string }>;
            return (
              <div key={index} className={`${styles.questionCard} ${field.disableByKey ? styles.questionFollowUp : ''}`}>
                <span className={styles.questionNumber}>{number}</span>
                <p className={styles.questionText}>{field.title}</p>
                <div className={styles.answerGroup} role="radiogroup" aria-label={field.title}>
                  {options.map((opt) => {
                    const value = String(opt.value ?? '');
                    const selected = field.value === value;
                    return (
                      <label key={value} className={`${styles.answer} ${selected ? styles.answerSelected : ''}`}>
                        <input
                          type="radio"
                          name={`question-${index}`}
                          value={value}
                          checked={selected}
                          onChange={() => actions.updateField(ref, value)}
                        />
                        {opt.label ?? value}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Section>
      <ConsentCheck state={state} actions={actions} t={t} />
    </>
  );
}

/* ────────────────────────────── Loading ────────────────────────────── */

function FormShimmer() {
  return (
    <div aria-hidden="true">
      <div className={`${styles.jobCard} ${styles.shimmerCard}`}>
        <span className={`${styles.shimmerLine} ${styles.shimmerEyebrow}`} />
        <span className={`${styles.shimmerLine} ${styles.shimmerTitle}`} />
        <span className={`${styles.shimmerLine} ${styles.shimmerMeta}`} />
      </div>
      <div className={styles.formCard}>
        <div className={styles.shimmerStepper}>
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className={`${styles.shimmerLine} ${styles.shimmerStep}`} />
          ))}
        </div>
        <div className={styles.stepBody}>
          <span className={`${styles.shimmerLine} ${styles.shimmerHeading}`} />
          <div className={styles.fieldsGrid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={styles.shimmerField}>
                <span className={`${styles.shimmerLine} ${styles.shimmerLabel}`} />
                <span className={`${styles.shimmerLine} ${styles.shimmerInput}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
